/* eslint-disable no-console */
import React, { useCallback, useEffect } from 'react'

import {
  QuestionMetricsAction,
  QuestionMetricsPayload,
} from '@/domain/model/question_metrics'
import { useAddNewQuestionMetric } from '@/queries/questionMetrics/useAddNewQuestionMetric'
import { storageQuestionMetrics } from '@/utils/storageQuestionMetrics/storageQuestionMetrics'

type Props = {
  children: React.ReactNode
}

export const TriggerUserActions = ({ children }: Props) => {
  const { addNewQuestionMetric } = useAddNewQuestionMetric()

  const trackMetric = async (
    event: React.SyntheticEvent<HTMLElement>,
    action: QuestionMetricsAction
  ) => {
    const target = event.target as HTMLElement
    if (!target) return

    const responseId = target?.getAttribute?.('data-response-id') || ''
    const formId = target?.getAttribute?.('data-form-id') || ''
    const packetId = target?.getAttribute?.('data-packet-id') || undefined

    const id = target?.id || ''
    const questionId = id?.match(/q-(\d+)/)?.[1] || ''

    const inputField = target?.getAttribute?.('data-input-field') || ''

    if (id) {
      const metricsPayload: QuestionMetricsPayload = {
        packet_id: packetId,
        form_id: formId,
        question_id: Number(questionId),
        response_id: responseId,
        action,
      }
      storageQuestionMetrics.set(metricsPayload)
      await addNewQuestionMetric(metricsPayload)
    }

    if (inputField) {
      const metricsPayload: QuestionMetricsPayload = {
        packet_id: packetId,
        document_id: '',
        input_field: inputField,
        response_id: responseId,
        action,
      }
      storageQuestionMetrics.set(metricsPayload)
      await addNewQuestionMetric(metricsPayload)
    }
  }

  const handleOnBlur = async (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement
    if (['radio', 'checkbox'].includes(target.type)) return
    const cleanValue = target?.value?.replace(
      /^(MM\/DD\/YYYY(\s(hh:mm\sA)?)?|hh:mm\sA)?$/,
      ''
    )

    if (cleanValue === '') await trackMetric(event, 'SKIPPED')
    if (cleanValue?.length) await trackMetric(event, 'ANSWERED')
  }

  const handleOnClick = async (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement

    if (!['radio', 'checkbox'].includes(target.type)) return

    if (target instanceof HTMLInputElement) {
      if (!target.checked) await trackMetric(event, 'SKIPPED')

      if (target.checked && target.value?.length)
        await trackMetric(event, 'ANSWERED')
    }
  }

  const handleOnFocus = async (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement
    if (['radio', 'checkbox'].includes(target.type)) return
    await trackMetric(event, 'FOCUSED')
  }

  const handleBeforeUnload = useCallback(async () => {
    const getStorageQuestionMetrics = storageQuestionMetrics.get()

    if (Object.keys(getStorageQuestionMetrics).length) {
      const formattedStorageQuestionMetrics: QuestionMetricsPayload = {
        ...getStorageQuestionMetrics,
        action: 'DROP-OFF',
      }
      await addNewQuestionMetric(formattedStorageQuestionMetrics)
      storageQuestionMetrics.remove()
    }
  }, [addNewQuestionMetric])

  useEffect(() => {
    const handleUnload = () => {
      handleBeforeUnload().catch(console.error)
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [handleBeforeUnload])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return React.cloneElement(children as React.ReactElement<any>, {
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onClick: handleOnClick,
  })
}
